import React from 'react';
import { motion } from 'framer-motion';
import { Lock, BookOpen, Crown, Shield, Sparkles, Zap, MessageSquare, ShieldCheck, Check } from 'lucide-react';
export function ValueStack({
  selectedBumps,
  setSelectedBumps
}: {
  selectedBumps: number[];
  setSelectedBumps: React.Dispatch<React.SetStateAction<number[]>>;
}) {
  const orderBumps = [{
    id: 0,
    title: 'El Acelerador de 7 Días',
    subtitle: 'Resultados Flash',
    description: 'Implementa el sistema completo en solo 7 días con un plan de acción diario paso a paso.',
    icon: Zap,
    color: 'from-orange-500 to-red-500',
    borderColor: 'border-orange-500/40',
    glowColor: 'shadow-orange-500/20'
  }, {
    id: 1,
    title: 'El Script "Aprobación Garantizada"',
    subtitle: 'Qué Decirle al Banco',
    description: 'Las palabras exactas para hablar con el banco y salir con tu línea de crédito aprobada.',
    icon: MessageSquare,
    color: 'from-blue-500 to-cyan-500',
    borderColor: 'border-blue-500/40',
    glowColor: 'shadow-blue-500/20'
  }, {
    id: 2,
    title: 'El Escudo Anti-Demandas',
    subtitle: 'Paz Mental',
    description: 'Protege tu patrimonio personal al 100%. Contratos y cláusulas que blindan tu negocio.',
    icon: ShieldCheck,
    color: 'from-emerald-500 to-green-500',
    borderColor: 'border-emerald-500/40',
    glowColor: 'shadow-emerald-500/20'
  }];
  const toggleBump = (id: number) => {
    setSelectedBumps(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };
  const items = [{
    name: 'Guía Imperio Americano (Libro Principal)',
    coverLine1: 'IMPERIO',
    coverLine2: 'AMERICANO',
    subtitle: 'El sistema completo paso a paso para escalar tu negocio en USA',
    value: 197,
    gradient: 'from-navy via-blue-900 to-navy-800',
    accent: 'gold',
    icon: Crown,
    badge: null
  }, {
    name: 'La Biblia de Bancos "Friendly" para Latinos',
    coverLine1: 'BIBLIA DE',
    coverLine2: 'BANCOS',
    subtitle: 'Los bancos exactos que aprueban a latinos con ITIN o SSN',
    value: 97,
    gradient: 'from-amber-700 via-gold-dark to-amber-800',
    accent: 'white',
    icon: BookOpen,
    badge: 'BONO #1'
  }, {
    name: 'ITIN Power: Crédito Comercial sin Social Security',
    coverLine1: 'ITIN',
    coverLine2: 'POWER',
    subtitle: 'Protocolo para obtener crédito business usando solo tu ITIN',
    value: 97,
    gradient: 'from-blue-800 via-blue-700 to-indigo-900',
    accent: 'gold',
    icon: Sparkles,
    badge: 'BONO #2'
  }, {
    name: 'Tu Escudo Legal (Manual de Contratos Pro)',
    coverLine1: 'ESCUDO',
    coverLine2: 'LEGAL',
    subtitle: 'Plantillas y contratos para blindar tu negocio legalmente',
    value: 97,
    gradient: 'from-gray-800 via-gray-700 to-gray-900',
    accent: 'gold',
    icon: Shield,
    badge: 'BONO #3'
  }];
  return <section id="pricing" className="py-8 md:py-14 bg-navy-900 relative overflow-hidden" data-id="element-194">
      <div className="absolute top-20 right-0 w-80 h-80 bg-gold/5 rounded-full blur-3xl" data-id="element-195" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" data-id="element-196" />

      <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-197">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8" data-id="element-198">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-199">
            <div className="h-[1px] w-10 bg-gradient-to-r from-transparent to-gold/50" data-id="element-200" />
            <span className="text-xs font-heading font-semibold text-gold/70 uppercase tracking-[0.3em]" data-id="element-201">
              Paquete Completo
            </span>
            <div className="h-[1px] w-10 bg-gradient-to-l from-transparent to-gold/50" data-id="element-202" />
          </div>
          <h2 className="text-3xl md:text-4xl text-white mb-4" data-id="element-203">
            Todo Lo Que Recibes Hoy
          </h2>
          <p className="text-gray-400 font-body text-lg" data-id="element-204">
            Un arsenal completo diseñado para tu éxito inmediato.
          </p>
        </motion.div>

        <div className="bg-navy-800/50 rounded-2xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-sm" data-id="element-205">
          <div className="p-5 md:p-10" data-id="element-206">
            <div className="space-y-5" data-id="element-207">
              {items.map((item, idx) => {
              const Icon = item.icon;
              return <motion.div key={idx} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.12
              }} className="flex items-center gap-4 md:gap-5 p-3 md:p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-300 group" data-id="element-208">
                    {/* Premium Book Cover */}
                    <div className="relative flex-shrink-0" data-id="element-209">
                      {item.badge && <div className="absolute -top-2 -right-2 z-20 bg-gold text-navy text-[8px] font-heading font-extrabold px-1.5 py-0.5 rounded-full shadow-lg shadow-gold/30" data-id="element-210">
                          {item.badge}
                        </div>}
                      <div className={`w-16 md:w-20 rounded-r-lg bg-gradient-to-br ${item.gradient} shadow-xl relative overflow-hidden border-l-[5px] ${item.accent === 'gold' ? 'border-gold' : 'border-white/60'} group-hover:shadow-2xl transition-shadow duration-300`} style={{
                    aspectRatio: '3/4'
                  }} data-id="element-211">
                        {/* Book shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-black/20" data-id="element-212" />
                        {/* Top decorative line */}
                        <div className={`absolute top-2 left-2 right-2 h-[1px] ${item.accent === 'gold' ? 'bg-gold/40' : 'bg-white/30'}`} data-id="element-213" />
                        {/* Icon & Cover Title */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-1.5 md:p-2 gap-0.5" data-id="element-214">
                          <Icon className={`w-3.5 h-3.5 md:w-4 md:h-4 ${item.accent === 'gold' ? 'text-gold/70' : 'text-white/70'}`} strokeWidth={1.5} data-id="element-215" />
                          <div className={`w-5 h-[1px] my-0.5 ${item.accent === 'gold' ? 'bg-gold/30' : 'bg-white/20'}`} data-id="element-216" />
                          <div className="text-white font-heading font-extrabold text-[7px] md:text-[9px] leading-tight text-center tracking-tight" data-id="element-217">
                            {item.coverLine1}
                          </div>
                          <div className={`font-heading font-extrabold text-[7px] md:text-[9px] leading-tight text-center tracking-tight ${item.accent === 'gold' ? 'text-gold' : 'text-white/90'}`} data-id="element-218">
                            {item.coverLine2}
                          </div>
                        </div>
                        {/* Bottom decorative line */}
                        <div className={`absolute bottom-2 left-2 right-2 h-[1px] ${item.accent === 'gold' ? 'bg-gold/40' : 'bg-white/30'}`} data-id="element-219" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-220">
                      <h4 className="font-heading font-bold text-white text-sm md:text-base leading-snug" data-id="element-221">
                        {item.name}
                      </h4>
                      <p className="text-gray-500 text-xs md:text-sm mt-1 leading-relaxed font-body hidden sm:block" data-id="element-222">
                        {item.subtitle}
                      </p>
                    </div>

                    {/* Value */}
                    <div className="flex flex-col items-end flex-shrink-0" data-id="element-223">
                      <span className="font-heading font-bold text-gray-500 line-through text-sm" data-id="element-224">
                        ${item.value}
                      </span>
                      <span className="text-[10px] text-gold/60 font-heading font-semibold uppercase tracking-wider" data-id="element-225">
                        Gratis
                      </span>
                    </div>
                  </motion.div>;
            })}
            </div>

            <div id="pricing-checkout" className="mt-10 pt-8 border-t border-white/10 text-center" data-id="element-226">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.6
            }} data-id="element-227">
                <div className="text-gray-400 font-heading font-semibold mb-2" data-id="element-228">
                  Valor Total:{' '}
                  <span className="line-through text-gray-500" data-id="element-229">$488</span>
                </div>
                <div className="text-sm text-gold/80 font-bold uppercase tracking-widest mb-2" data-id="element-230">
                  Precio de Lanzamiento
                </div>
                <div className="text-6xl md:text-7xl font-heading font-extrabold text-gold mb-1" data-id="element-231">
                  ${(6.36 + selectedBumps.length * 9).toFixed(2)}
                </div>
                {selectedBumps.length > 0 && <p className="text-sm text-gray-400 font-heading mb-4" data-id="element-232">
                    $6.36 + {selectedBumps.length} bump
                    {selectedBumps.length > 1 ? 's' : ''} ($
                    {(selectedBumps.length * 9).toFixed(2)})
                  </p>}
                {selectedBumps.length === 0 && <div className="mb-4" data-id="element-233" />}

                {/* Order Bumps — integrated below price */}
                <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-dashed border-gold/25" data-id="element-234">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-235">
                    <span className="text-base" data-id="element-236">⚡</span>
                    <h3 className="text-sm md:text-base font-heading font-extrabold text-gold uppercase tracking-wide" data-id="element-237">
                      Agrega Aceleradores por $9 c/u
                    </h3>
                    <span className="text-base" data-id="element-238">⚡</span>
                  </div>

                  <div className="space-y-2" data-id="element-239">
                    {orderBumps.map(bump => {
                    const BumpIcon = bump.icon;
                    const isSelected = selectedBumps.includes(bump.id);
                    return <div key={bump.id} onClick={() => toggleBump(bump.id)} role="button" tabIndex={0} onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') toggleBump(bump.id);
                    }} className={`w-full text-left px-3 py-3 rounded-lg border transition-all duration-300 relative overflow-hidden group cursor-pointer active:scale-[0.98] ${isSelected ? `${bump.borderColor} bg-white/[0.06] shadow-md ${bump.glowColor}` : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'}`} data-id="element-240">
                          {isSelected && <div className={`absolute inset-0 bg-gradient-to-r ${bump.color} opacity-[0.04]`} data-id="element-241" />}

                          <div className="relative z-10 flex items-center gap-3" data-id="element-242">
                            {/* Checkbox */}
                            <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all duration-300 ${isSelected ? `bg-gradient-to-br ${bump.color} border-transparent shadow-sm` : 'border-white/30 bg-transparent'}`} data-id="element-243">
                              {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-244" />}
                            </div>

                            {/* Icon */}
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${bump.color} flex items-center justify-center flex-shrink-0 ${isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-80'} transition-opacity`} data-id="element-245">
                              <BumpIcon className="w-4 h-4 text-white" strokeWidth={2} data-id="element-246" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0" data-id="element-247">
                              <h4 className="font-heading font-bold text-white text-xs md:text-sm leading-tight" data-id="element-248">
                                {bump.title}
                              </h4>
                              <p className="text-gray-500 text-[10px] md:text-xs font-body leading-snug mt-0.5 hidden sm:block" data-id="element-249">
                                {bump.description}
                              </p>
                            </div>

                            {/* Price */}
                            <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-250">
                              <span className="text-gray-500 text-xs font-heading line-through" data-id="element-251">
                                $97
                              </span>
                              <span className="font-heading font-extrabold text-gold text-base" data-id="element-252">
                                $9
                              </span>
                            </div>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>

                <a href="https://manosquegeneran.com/cart/48451070951651:1" target="_blank" rel="noopener noreferrer" onClick={() => {
                if ((window as any).fbq) {
                  ;
                  (window as any).fbq('track', 'AddToCart', {
                    value: 6.36 + selectedBumps.length * 9,
                    currency: 'USD',
                    content_name: 'Imperio Americano',
                    content_type: 'product',
                    num_items: 1 + selectedBumps.length
                  });
                }
              }} className="w-full md:w-auto px-12 py-5 bg-gold hover:bg-gold-dark text-navy font-heading font-bold text-xl rounded-xl transition-all duration-300 shadow-xl shadow-gold/20 hover:shadow-glow transform hover:-translate-y-1 flex items-center justify-center gap-2 mx-auto" data-id="element-253">
                  <Lock className="w-5 h-5" data-id="element-254" />
                  Comprar Ahora — $
                  {(6.36 + selectedBumps.length * 9).toFixed(2)}
                </a>
                <p className="text-gray-500 text-sm mt-4 flex items-center justify-center gap-1" data-id="element-255">
                  Pago 100% seguro y encriptado
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}